import {
  INBOX_FILTER_DEFAULT,
  MAIL_COLOR_LABELS,
  inboxFilterIsActive,
  type InboxListFilter,
  type MailColorLabel,
} from "@/lib/mail/messageLabels";

const STORAGE_KEY = "mail-inbox-filter-v1";

const COLOR_IDS = new Set(MAIL_COLOR_LABELS.map((c) => c.id));

function filterKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function normalizeColor(value: unknown): MailColorLabel | null {
  if (typeof value !== "string" || !COLOR_IDS.has(value as MailColorLabel)) {
    return null;
  }
  return value as MailColorLabel;
}

function normalizeFilter(value: unknown): InboxListFilter {
  if (!value || typeof value !== "object") return { ...INBOX_FILTER_DEFAULT };
  const raw = value as Partial<InboxListFilter>;
  return {
    unreadOnly: !!raw.unreadOnly,
    importantOnly: !!raw.importantOnly,
    favoriteOnly: !!raw.favoriteOnly,
    attachmentsOnly: !!raw.attachmentsOnly,
    colorLabel: normalizeColor(raw.colorLabel),
  };
}

function readAll(): Record<string, InboxListFilter> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, normalizeFilter(value)])
    );
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, InboxListFilter>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function readInboxListFilter(accountId: number, folder: string): InboxListFilter {
  return readAll()[filterKey(accountId, folder)] ?? { ...INBOX_FILTER_DEFAULT };
}

export function writeInboxListFilter(
  accountId: number,
  folder: string,
  filter: InboxListFilter
): void {
  const map = readAll();
  const key = filterKey(accountId, folder);
  if (!inboxFilterIsActive(filter)) {
    delete map[key];
  } else {
    map[key] = normalizeFilter(filter);
  }
  writeAll(map);
}
